import clsx from 'clsx';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Actions, Card, CardButton } from '@gitbook/spine-react';

import { ContentKitButton } from './ContentKitButton';
import { ContentKitIcon } from './ContentKitIcon';
import { ContentKitImage } from './ContentKitImage';
import { ensureStackElements } from './ContentKitStack';
import type { ContentKitElementProps } from './types';

export function ContentKitCard(
    props: ContentKitElementProps<api.ContentKitCard>
): React.ReactElement {
    const { element, renderElements, dispatchAction } = props;

    const header = element.title ? (
        <div className="flex items-center">
            {element.icon ? (
                <div className="shrink-0 p-4 pr-0">
                    {typeof element.icon === 'string' ? (
                        <ContentKitIcon icon={element.icon} size={18} className="text-muted" />
                    ) : (
                        <ContentKitImage {...props} element={element.icon} style={{ width: 18 }} />
                    )}
                </div>
            ) : null}
            <div className="flex flex-1 flex-col gap-1 p-4">
                <h4 className="font-medium">{element.title}</h4>
                {element.hint ? (
                    <div className="text-muted">
                        {typeof element.hint === 'string'
                            ? element.hint
                            : renderElements(
                                  'inline',
                                  ensureStackElements('hstack', element.hint, 'start')
                              )}
                    </div>
                ) : null}
            </div>
            {element.buttons && element.buttons.length > 0 ? (
                <Actions className="px-4 py-2">
                    {element.buttons.map((button, index) => (
                        <ContentKitButton
                            key={index}
                            {...props}
                            element={button}
                            buttonDefaultKind="blank"
                        />
                    ))}
                </Actions>
            ) : null}
        </div>
    ) : null;

    const content = (
        <div className="flex flex-col">
            {header}
            {element.children && element.children.length > 0 ? (
                <div className={clsx('p-4', header && 'pt-0')}>
                    {renderElements('block', ensureStackElements('vstack', element.children))}
                </div>
            ) : null}
        </div>
    );

    const { onPress } = element;

    if (onPress) {
        return (
            <CardButton
                onPress={() => {
                    dispatchAction(onPress);
                }}
            >
                {content}
            </CardButton>
        );
    }

    return <Card>{content}</Card>;
}
